import { CopywritingCampaign } from "../../List/pages/ListEditCopywriting/ListEditCopywriting";
import { ListPerson } from "../../List/schema/list";

/**
 * Resolves the list's default campaign config for a given prospect,
 * ignoring any custom (prospect-level) campaign config.
 *
 * Resolution order:
 * 1. If the prospect has a campaignId assigned, use that campaign from the list
 * 2. Language-based default campaign (matching prospect's language)
 * 3. The list's default campaign (isDefault === true)
 * 4. Any valid non-default campaign as fallback
 *
 * @returns The resolved campaign config from the list, or undefined if none found
 */
export const getListCampaignConfig = (prospect: ListPerson): CopywritingCampaign | undefined => {
	const allCampaigns = prospect.list.campaignConfig as {
		[key: string]: CopywritingCampaign;
	};

	if (!allCampaigns) {
		return undefined;
	}

	const allValidCampaigns = Object.keys(allCampaigns)?.filter(
		(campaign) => prospect.list.campaignConfig[campaign].isValid
	);

	if (!allValidCampaigns || allValidCampaigns.length === 0) {
		return undefined;
	}

	if (prospect.campaignId && prospect.list.campaignConfig[prospect.campaignId]) {
		return prospect.list.campaignConfig[prospect.campaignId] as CopywritingCampaign;
	}

	const defaultCampaignId = allValidCampaigns.find(
		(campaign) => prospect.list.campaignConfig[campaign].isDefault
	);

	const defaultCampaign: CopywritingCampaign = {
		...(allCampaigns?.[defaultCampaignId || ""] || {}),
		id: defaultCampaignId,
	} as CopywritingCampaign;

	const languages = prospect.prospect?.language
		?.split(",")
		?.map((lang: string) => lang.trim().toLowerCase());

	const languageBasedDefaultCampaignId = allValidCampaigns.find((campaign) =>
		prospect?.list?.campaignConfig?.[campaign]?.languages?.some((lang: string) =>
			languages?.includes(lang.toLowerCase())
		)
	);

	const languageBasedDefaultCampaign = languageBasedDefaultCampaignId
		? ({
				...(allCampaigns[languageBasedDefaultCampaignId || ""] || {}),
				id: languageBasedDefaultCampaignId || "",
			} as CopywritingCampaign)
		: undefined;

	const pickNonDefaultCampaignAsAlternativeId = allValidCampaigns.find(
		(campaign) => prospect.list.campaignConfig[campaign].isDefault === false
	);

	const pickNonDefaultCampaignAsAlternative = pickNonDefaultCampaignAsAlternativeId
		? ({
				...(allCampaigns[pickNonDefaultCampaignAsAlternativeId] || {}),
				id: pickNonDefaultCampaignAsAlternativeId,
			} as CopywritingCampaign)
		: undefined;

	return languageBasedDefaultCampaign || defaultCampaign || pickNonDefaultCampaignAsAlternative;
};
